import { Provider } from "risa-data-model";
import { Hit } from "../../../../data-model/algoliaSearchModel";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { BOOptionModel } from "../../../../data-model/boOptionModel";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../utils/stringModifications";

// Medical PA Orders specific filter keys for Algolia
const MedicalPaOrdersAlgoliaFilterKeys = {
  RegimenNames: "regimen_name",
  AuthVerificationStatus: "auth_status",
  BoStatus: "bo_status",
  EvWriteBackStatus: "ev_write_back_status",
  DocumentUploadStatus: "document_upload_status",
  DateOfService: "date_of_service",
  CreatedAt: "created_at",
  DateOfWork: "date_of_work",
  PrimaryPayerName: "primary_payer_name",
  PractitionerName: "practitioner_name",
  Location: "location",
  AuthStatus: "auth_status",
  Assignee: "assigned_to",
  RegimenType: "regimen_type",
  EvPrimary: "ev_bv_primary",
};

const getUniqueDrugNamesFromMedicalPaOrdersOfAlgolia = (
  medicalPaOrders: Hit[],
) => {
  const allDrugNames = medicalPaOrders.map((order) => order?.regimen_name);
  const uniqueDrugNames = [...new Set(allDrugNames)];
  return uniqueDrugNames;
};

const getUniquePlanDetailsFromMedicalPaOrdersOfAlgolia = (
  medicalPaOrders: Hit[],
) => {
  const allPlanDetails = medicalPaOrders.map(
    (order) => order?.primary_payer_name,
  );
  const uniquePlanDetails = [...new Set(allPlanDetails)];
  return uniquePlanDetails;
};

const getUniquePractitionerNamesFromMedicalPaOrdersOfAlgolia = (
  medicalPaOrders: Hit[],
) => {
  const allPractitionerNames = medicalPaOrders.map(
    (order) => order?.practitioner_name,
  );
  const uniquePractitionerNames = [...new Set(allPractitionerNames)];
  return uniquePractitionerNames;
};

const getUniqueLocationsFromMedicalPaOrdersOfAlgolia = (
  medicalPaOrders: Hit[],
) => {
  const allLocations = medicalPaOrders.map((order) => order?.location);
  const uniqueLocations = [...new Set(allLocations)];
  return uniqueLocations;
};

export const createFilterForDrugNamesForAlgolia = (orders: Hit[]) => {
  const drugNames = getUniqueDrugNamesFromMedicalPaOrdersOfAlgolia(orders);
  const drugNameOptions = drugNames.map((drugName) => ({
    label: drugName ?? "",
    value: drugName ?? "",
  }));
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.RegimenNames,
    label: "Medication",
    options: drugNameOptions.sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

export const createFilterForDateOfServiceForAlgolia = () => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.DateOfService,
    label: "Date of Service",
    options: [],
    type: "date" as const,
  };
};

export const createFilterForCreatedAtForAlgolia = () => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.CreatedAt,
    label: "Date of Creation",
    options: [],
    type: "date" as const,
  };
};

export const createFilterForDateOfWorkForAlgolia = () => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.DateOfWork,
    label: "Date of Work",
    options: [],
    type: "date" as const,
  };
};

export const createFilterForBoStatusForAlgolia = (
  boOptions: BOOptionModel[],
) => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.BoStatus,
    label: "BO Status",
    options: boOptions
      .map((boOption) => ({
        label: boOption.label,
        value: boOption.value,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    type: "string" as const,
  };
};

export const createFilterForEvWriteBackStatusForAlgolia = () => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.EvWriteBackStatus,
    label: "Write Back Status",
    options: Object.values(EVWriteBackStatus).map((evWriteBackOption) => ({
      label: capitalizeWordsSeperatedByUnderScore(evWriteBackOption),
      value: evWriteBackOption,
    })),
    type: "string" as const,
  };
};

export const createFilterForDocumentUploadStatusForAlgolia = () => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.DocumentUploadStatus,
    label: "Document Upload Status",
    type: "string" as const,
    options: Object.values(DocWriteBackStatus).map((docWriteBackOption) => ({
      label: capitalizeWordsSeperatedByUnderScore(docWriteBackOption),
      value: docWriteBackOption,
    })),
  };
};

export const createFilterOptionsForPlanDetailsForAlgolia = (orders: Hit[]) => {
  const planDetails = getUniquePlanDetailsFromMedicalPaOrdersOfAlgolia(orders);
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.PrimaryPayerName,
    label: "Insurance Plans",
    options: planDetails.map((planDetail) => ({
      label: planDetail ?? "",
      value: planDetail ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsBasedOnPractitionerNameForAlgolia = (
  orders: Hit[],
) => {
  const practitionerNames =
    getUniquePractitionerNamesFromMedicalPaOrdersOfAlgolia(orders);
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.PractitionerName,
    label: "Practitioner Name",
    options: practitionerNames.map((practitionerName) => ({
      label: practitionerName ?? "",
      value: practitionerName ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsBasedOnLocationForAlgolia = (orders: Hit[]) => {
  const locations = getUniqueLocationsFromMedicalPaOrdersOfAlgolia(orders);
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.Location,
    label: "Location",
    options: locations.map((location) => ({
      label: location ?? "",
      value: location ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsBasedOnAuthStatusForAlgolia = (
  options: AuthStatusOptionModel[],
) => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.AuthStatus,
    label: "Auth Status",
    options: options.map((option) => ({
      label: option.text,
      value: option.id,
    })),
    type: "string" as const,
  };
};

// Medical PA specific filter - not available in external orders
export const createFiltersForAssigneeForAlgolia = (
  allProviders: Provider[],
) => {
  const providerOptions = allProviders.map((provider) => ({
    label: generateFullName(
      provider?.FirstName ?? "",
      "",
      provider?.LastName ?? "",
    ),
    value: provider?.DocID ?? "",
  }));

  const unAssignedOption = {
    label: "Unassigned",
    value: "unassigned",
  };

  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.Assignee,
    label: "Assignee",
    options: [
      ...providerOptions.sort((a, b) => a.label.localeCompare(b.label)),
      unAssignedOption,
    ],
    type: "string" as const,
  };
};

export const createFilterForRegimenNamesFromAlgoliaMultiFacetsCount = (
  options: string[],
) => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.RegimenNames,
    label: "Regimen Names",
    options: options.map((regimenName) => ({
      label: regimenName ?? "",
      value: regimenName ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsForPlanDetailsFromAlgoliaMultiFacetsCount = (
  options: string[],
) => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.PrimaryPayerName,
    label: "Insurance Plans",
    options: options.map((planDetail) => ({
      label: planDetail ?? "",
      value: planDetail ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsBasedOnPractitionerNameFromAlgoliaMultiFacetsCount =
  (options: string[]) => {
    return {
      id: MedicalPaOrdersAlgoliaFilterKeys.PractitionerName,
      label: "Practitioner Name",
      options: options.map((practitionerName) => ({
        label: practitionerName ?? "",
        value: practitionerName ?? "",
      })),
      type: "string" as const,
    };
  };

export const createFilterOptionsBasedOnLocationFromAlgoliaMultiFacetsCount = (
  options: string[],
) => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.Location,
    label: "Location",
    options: options.map((location) => ({
      label: location ?? "",
      value: location ?? "",
    })),
    type: "string" as const,
  };
};

export const createFilterOptionsForRegimenType = () => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.RegimenType,
    label: "Regimen Type",
    options: [
      {
        label: "Single Agent",
        value: "single_agent",
      },
      {
        label: "Multi Agent",
        value: "multi_agent",
      },
    ],
    type: "string" as const,
  };
};

export const createFilterOptionsForEvPrimary = (options: string[]) => {
  return {
    id: MedicalPaOrdersAlgoliaFilterKeys.EvPrimary,
    label: "EV Primary Status",
    options: options.map((option) => ({
      label: capitalizeWordsSeperatedByUnderScore(option ?? ""),
      value: option ?? "",
    })),
    type: "string" as const,
  };
};
